import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Collapse,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Description as FileIcon, ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { GetPurchaseVoucherList, exportPurchaseVouchers } from "../../../Services/Services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { Tooltip } from "@mui/material";

import { useLocation } from "react-router-dom";

function MaterialInwardRegister() {
  // ===== States =====
  const [purchaseVouchers, setPurchaseVouchers] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Date Range States
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({ from: "", to: "" });
  const [excelLoading, setExcelLoading] = useState(false); // 🆕 Excel loading state

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedVoucher, setExpandedVoucher] = useState(null);
  const [filters, setFilters] = useState({
    date_range: "today",
    po_no: "",
    supplier: "",
    grn_no: "",
    status: "Approved",
  });

  const location = useLocation();

  // ===== Helper Functions =====
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };
function formatOnlyDate(dateTimeString) {
  if (!dateTimeString) return "N/A";

  // Take only the date part (before T)
  const datePart = dateTimeString.split("T")[0];  // "2025-12-02"

  const [year, month, day] = datePart.split("-");

  return `${day}-${month}-${year}`;
}

  const handleExcelExport = async () => {
    setExcelLoading(true);
    try {
      const blob = await exportPurchaseVouchers(filters);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Material_inward_register_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };
  // ===== Fetch Function =====
  const fetchVouchers = async (pageNum = 1, reset = false, filterValues = filters) => {
    setLoading(true);
    try {
      const params = { ...filterValues };

      if (
        filterValues.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      console.log("📤 Material Inward API Params:", params);

      const data = await GetPurchaseVoucherList(params, pageNum);

      if (reset || pageNum === 1) {
        setPurchaseVouchers(data.results || []);
      } else {
        setPurchaseVouchers((prev) => [...prev, ...(data.results || [])]);
      }

      setHasMore(data.next !== null);
      setPage(pageNum);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load vouchers");
    } finally {
      setLoading(false);
    }
  };

  // ===== useEffects =====

  // ✅ SINGLE useEffect for fetching data on filter change
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchVouchers(1, true, filters);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, customDateRange]);

  useEffect(() => {
    if (location.state?.edited) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // useEffect(() => {
  //   document.body.style.overflow = "hidden";
  //   return () => {
  //     document.body.style.overflow = "auto";
  //   };
  // }, []);

  // ===== Handlers =====
  const fetchMoreData = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      fetchVouchers(nextPage, false, filters);
    }
  };

  const handleReset = () => {
    setFilters({
      item_code: "",
      name: "",
      category: "",
      sub_category: "",
      date_range: "this_month",
    });
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
    setPurchaseVouchers([]);
    setPage(1);
    setHasMore(true);
  };

  // ✅ Handle Custom Date Range Apply
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Handle Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Filter Section */}
      <Box sx={{ mb: 2, mt: -5 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Dropdown */}
          <Grid item xs={12} sm={2.5}>
            <TextField
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                  customDateRange.from &&
                  customDateRange.to
                  ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                    ? "Today"
                    : filters.date_range === "yesterday"
                      ? "Yesterday"
                      : filters.date_range === "this_week"
                        ? "This Week"
                        : filters.date_range === "previous_week"
                          ? "Previous Week"
                          : filters.date_range === "this_month"
                            ? "This Month"
                            : filters.date_range === "current_year"
                              ? "Current Year"
                              : filters.date_range === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={2.5}>
            <TextField
              label="GRN No"
              name="grn_no"
              size="small"
              value={filters.grn_no}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={2.5}>
            <TextField
              label="PO No"
              name="po_no"
              size="small"
              value={filters.po_no}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={2.5}>
            <TextField
              label="Supplier"
              name="supplier"
              size="small"
              value={filters.supplier}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: "35px",
                whiteSpace: "nowrap",
                borderRadius: "0",
                fontSize: "0.7rem",
              }}
              fullWidth
            >
              Reset
            </Button>
          </Grid>

          <Grid item xs={12} sm={1}>
            <Tooltip
              title={excelLoading ? "Exporting..." : "Download Excel"}
              arrow
            >
              <span>
                <IconButton
                  color="success"
                  size="small"
                  onClick={handleExcelExport}
                  disabled={excelLoading}
                  sx={{
                    borderRadius: "4px",
                    padding: "6px",
                  }}
                >
                  {excelLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <FileIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Date Range Menu - UPDATED */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{
          sx: { width: 350, maxHeight: 500 },
        }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setFilters((prev) => ({ ...prev, date_range: range.key }));
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}>
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: tempCustomDateRange.to || new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* Conditional Rendering: Mobile vs Desktop */}
      {isMobile ? (
        /* 📱 MOBILE VIEW */
        <InfiniteScroll
          dataLength={purchaseVouchers.length}
          next={fetchMoreData}
          hasMore={hasMore}
          height={"75vh"}
          loader={
            <Box sx={{ textAlign: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          }
          endMessage={
            <Typography
              sx={{ textAlign: "center", p: 2, color: "text.secondary" }}
            >
              {purchaseVouchers.length === 0 ? "No vouchers found" : "No more data"}
            </Typography>
          }
        >
          <Box sx={{ bgcolor: "background.paper" }}>
            {purchaseVouchers.map((voucher, voucherIndex) => {
              const totalItems = voucher.items?.length || 0;

              return (
                <Box key={`voucher-${voucherIndex}`}>
                  {/* Header Card */}
                  <Box
                    onClick={() => {
                      setExpandedVoucher(
                        expandedVoucher === voucherIndex ? null : voucherIndex
                      );
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      cursor: "pointer",
                      bgcolor: voucherIndex % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:active": { bgcolor: "#e3f2fd" },
                      borderLeft: "4px solid #2196f3",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "#666",
                            minWidth: 25,
                          }}
                        >
                          #{voucherIndex + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                            flex: 1,
                          }}
                        >
                          {voucher.grn_no}
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: "#2196f3",
                            color: "#fff",
                            borderRadius: "12px",
                            px: 1,
                            py: 0.2,
                            fontSize: "0.65rem",
                            fontWeight: 600,
                          }}
                        >
                          {totalItems} {totalItems === 1 ? "Item" : "Items"}
                        </Box>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "#666",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>
                          {voucher.supplier_name || "N/A"}
                        </span>
                        <span style={{ color: "#999" }}>•</span>
                        <span style={{ fontSize: "0.7rem" }}>
                          {formatOnlyDate(voucher.created_at)}
                        </span>
                      </Typography>
                    </Box>
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {expandedVoucher === voucherIndex ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* Collapsible Details */}
                  <Collapse
                    in={expandedVoucher === voucherIndex}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: voucherIndex % 2 === 0 ? "#f8f9fa" : "#fafafa",
                        borderLeft: "4px solid #2196f3",
                      }}
                    >
                      {/* Voucher Info */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 0.8,
                          mb: 1.5,
                          pb: 1,
                          borderBottom: "2px solid #e0e0e0",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            GRN NO
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#1976d2",
                            }}
                          >
                            {voucher.grn_no}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            PO NO
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#1976d2",
                            }}
                          >
                            {voucher.purchase_order || "-"}
                          </Typography>
                        </Box>

                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            SUPPLIER
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "#333",
                            }}
                          >
                            {voucher.supplier_name || "-"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            DATE
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {formatOnlyDate(voucher.created_at)}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            TOTAL ITEMS
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              color: "#4caf50",
                            }}
                          >
                            {totalItems}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Items Header */}
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          color: "#666",
                          mb: 1,
                          letterSpacing: "0.5px",
                        }}
                      >
                        ITEMS ({totalItems})
                      </Typography>

                      {/* Items List */}
                      {totalItems > 0 ? (
                        voucher.items.map((item, itemIndex) => (
                          <Box
                            key={`item-${itemIndex}`}
                            sx={{
                              mb: 1.5,
                              p: 1,
                              bgcolor: "#fff",
                              borderRadius: "4px",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#1976d2",
                                mb: 0.8,
                              }}
                            >
                              Item #{itemIndex + 1}
                            </Typography>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 0.8,
                              }}
                            >
                              <Box sx={{ gridColumn: "1 / -1" }}>
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    color: "#999",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  ITEM NAME
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 500,
                                    color: "#333",
                                  }}
                                >
                                  {item.item_name || "-"}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    color: "#999",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  CATEGORY
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 500,
                                    color: "#555",
                                  }}
                                >
                                  {item.category_name || "-"}
                                </Typography>
                              </Box>

                              {/* <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    color: "#999",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  SUB CATEGORY
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 500,
                                    color: "#555",
                                  }}
                                >
                                  {item.sub_category_name || "-"}
                                </Typography>
                              </Box> */}

                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    color: "#999",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  ITEM TYPE
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 500,
                                    color: "#555",
                                  }}
                                >
                                  {item.item_type || "-"}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    color: "#999",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  RECEIVED QTY
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 600,
                                    color: "#4caf50",
                                  }}
                                >
                                  {item.received_qty || 0}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    color: "#999",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  REJECTED QTY
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 600,
                                    color: "#f44336",
                                  }}
                                >
                                  {item.rejected_qty || 0}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    color: "#999",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  RATE
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 600,
                                    color: "#9c27b0",
                                  }}
                                >
                                  ₹{item.rate || 0}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#999",
                            textAlign: "center",
                            py: 2,
                          }}
                        >
                          No items found
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                  <Divider />
                </Box>
              );
            })}
          </Box>
        </InfiniteScroll>
      ) : (
        /* 💻 DESKTOP VIEW - UNCHANGED */
        <InfiniteScroll
          dataLength={purchaseVouchers.length}
          next={fetchMoreData}
          hasMore={hasMore}
          scrollableTarget="item-table"
        >
          <TableContainer
            component={Paper}
            id="item-table"
            sx={{
              maxHeight: "80vh",
              overflowX: { xs: "auto", sm: "unset" },
              overflowY: "auto",
              width: "100%",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: "max-content",
                "& td, & th": {
                  borderBottom: "1px solid #ccc",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    margin: 0,
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#2196f3" : "#333",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                      minWidth: 40,
                      padding: "0px",
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>GRN No</TableCell>
                  <TableCell>PO No</TableCell>
                  <TableCell align="right">Supplier Name</TableCell>
                  <TableCell>Category</TableCell>
                  {/* <TableCell>Sub Category</TableCell> */}
                  {/* <TableCell>Item Type</TableCell> */}
                  <TableCell align="right">Item Name</TableCell>
                  <TableCell>Rec Qty</TableCell>
                  <TableCell>Rem Qty</TableCell>
                  <TableCell align="right">Rate</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {purchaseVouchers.map((voucher, voucherIndex) => {
                  const itemCount = voucher.items.length;

                  return (
                    <React.Fragment key={voucherIndex}>
                      {voucher.items.map((item, itemIndex) => (
                        <TableRow
                          hover
                          key={`${voucherIndex}-${itemIndex}`}
                          sx={{
                            backgroundColor:
                              voucherIndex % 2 === 0 ? "#fafafa" : "#ffffff",
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                              "& td": { backgroundColor: "#e3f2fd !important" },
                            },
                            "& td": {
                              paddingTop: "2px !important",
                              paddingBottom: "2px !important",
                              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              color: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "white"
                                  : "inherit",
                            },
                          }}
                        >
                          {itemIndex === 0 && (
                            <TableCell
                              align="center"
                              rowSpan={itemCount}
                              sx={{
                                position: "sticky",
                                left: 0,
                                backgroundColor:
                                  voucherIndex % 2 === 0
                                    ? "#fafafa !important"
                                    : "#ffffff !important",
                                zIndex: 2,
                                minWidth: 50,
                              }}
                            >
                              {voucherIndex + 1}
                            </TableCell>
                          )}

                          {itemIndex === 0 && (
                            <TableCell
                              rowSpan={itemCount}
                              sx={{
                                backgroundColor:
                                  voucherIndex % 2 === 0
                                    ? "#fafafa !important"
                                    : "#ffffff !important",
                              }}
                            >
                              {formatOnlyDate(voucher.created_at)}
                            </TableCell>
                          )}

                          {itemIndex === 0 && (
                            <TableCell
                              rowSpan={itemCount}
                              sx={{
                                backgroundColor:
                                  voucherIndex % 2 === 0
                                    ? "#fafafa !important"
                                    : "#ffffff !important",
                              }}
                            >
                              {voucher.grn_no}
                            </TableCell>
                          )}

                          {itemIndex === 0 && (
                            <TableCell
                              align="right"
                              rowSpan={itemCount}
                              sx={{
                                backgroundColor:
                                  voucherIndex % 2 === 0
                                    ? "#fafafa !important"
                                    : "#ffffff !important",
                              }}
                            >
                              {voucher.purchase_order}
                            </TableCell>
                          )}

                          {itemIndex === 0 && (
                            <TableCell
                              align="right"
                              rowSpan={itemCount}
                              sx={{
                                backgroundColor:
                                  voucherIndex % 2 === 0
                                    ? "#fafafa !important"
                                    : "#ffffff !important",
                              }}
                            >
                              {voucher.supplier_name}
                            </TableCell>
                          )}

                          <TableCell
                            sx={{
                              backgroundColor:
                                voucherIndex % 2 === 0
                                  ? "#fafafa !important"
                                  : "#ffffff !important",
                            }}
                          >
                            {item.category_name || "-"}
                          </TableCell>
                          {/* <TableCell
                            sx={{
                              backgroundColor:
                                voucherIndex % 2 === 0
                                  ? "#fafafa !important"
                                  : "#ffffff !important",
                            }}
                          >
                            {item.sub_category_name || "-"}
                          </TableCell> */}
                          {/* <TableCell
                            sx={{
                              backgroundColor:
                                voucherIndex % 2 === 0
                                  ? "#fafafa !important"
                                  : "#ffffff !important",
                            }}
                          >
                            {item.item_type || "-"}
                          </TableCell> */}
                          <TableCell
                            sx={{
                              backgroundColor:
                                voucherIndex % 2 === 0
                                  ? "#fafafa !important"
                                  : "#ffffff !important",
                            }}
                            align="right"
                          >
                            {item.item_name || "-"}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor:
                                voucherIndex % 2 === 0
                                  ? "#fafafa !important"
                                  : "#ffffff !important",
                            }}
                          >
                            {item.received_qty || 0}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor:
                                voucherIndex % 2 === 0
                                  ? "#fafafa !important"
                                  : "#ffffff !important",
                            }}
                            align="right"
                          >
                            {item.rejected_qty || 0}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor:
                                voucherIndex % 2 === 0
                                  ? "#fafafa !important"
                                  : "#ffffff !important",
                            }}
                            align="right"
                          >
                            {item.rate || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      )}
    </Box>
  );


}

export default MaterialInwardRegister